import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SearchIcon } from 'assets/icons';
import {
  getAscOrdersFilters,
  getHasAscOrderOtherFilters,
  resetAscOrdersFilter,
  resetCdeItAscOrdersFilter,
  setAscOrdersFilter,
  setCdeItAscOrdersFilter,
} from 'domains/order/Order.store';
import {
  AscOrderFilterAndSortFieldLocal,
  AscOrdersFilterAndSortField,
  AscOrderStatus,
  DATE_FROM,
  DATE_TO,
} from 'domains/order/Order.types';
import { getUserRights, UserRole } from 'domains/user';
import { FlexWrapper } from 'pages/MyOrdersPage/OrderClaims/Filter/ClaimsFilter';
import { handleRangeFilterChange } from 'pages/MyOrdersPage/orders.utils';
import { theme } from 'styles';
import { Flex, Icon, Input, MarginBox, Select, Text } from 'UI';
import RangePicker from 'UI/DatePicker/RangePicker';
import { getData } from 'utils';

const AscOrdersFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector(getAscOrdersFilters);
  const hasOtherFilters = useSelector(getHasAscOrderOtherFilters);
  const userRoles = getData(useSelector(getUserRights)) ?? [];
  const isCdeIt = !!userRoles.find((x) => x === UserRole.R1_ORDER_AND_CLAIM);

  const updateFilters = (field: AscOrderFilterAndSortFieldLocal, value: string) => {
    dispatch(isCdeIt ? setCdeItAscOrdersFilter({ field, value }) : setAscOrdersFilter({ field, value }));
  };

  useEffect(() => {
    if (isCdeIt) {
      dispatch(resetCdeItAscOrdersFilter());
    }
  }, [isCdeIt, dispatch]);

  const searchAddon = (
    <Icon
      IconComponent={SearchIcon}
      size={14}
      display={'block'}
      mt={'auto'}
      mb={'auto'}
      color={theme.color.brand_black}
    />
  );

  const InputField = ({
    placeholder,
    onChangeDebounced,
    value,
  }: {
    placeholder: string;
    onChangeDebounced: (value: string) => void;
    value?: string;
  }) => {
    const [stateValue, setStateValue] = useState<string | undefined>(value);

    useEffect(() => {
      setStateValue(value);
    }, [value]);

    return (
      <Input
        value={stateValue}
        onChange={setStateValue}
        onChangeDebounced={onChangeDebounced}
        placeholder={placeholder}
        bordered
        suffix={searchAddon}
      />
    );
  };

  return (
    <FlexWrapper direction={'column'} gap={30}>
      <Flex align={'center'} gap={30}>
        <Text type="heading4" disableGutter>
          {t('common.filters', 'Filters')}
        </Text>
        <MarginBox mt={3}>
          <Text
            type="heading5"
            color="white"
            displayStyle={'link'}
            disableGutter
            hoverUnderLine
            cursor={'pointer'}
            onClick={() => {
              dispatch(isCdeIt ? resetCdeItAscOrdersFilter() : resetAscOrdersFilter());
            }}
          >
            {t('common.action.reset_filters', 'Reset filters')}
          </Text>
        </MarginBox>
      </Flex>
      <Flex gap={30}>
        <InputField
          value={filters.get(AscOrdersFilterAndSortField.ORDER)}
          placeholder={t('order.order_detail.order_number', 'Order Number')}
          onChangeDebounced={(value) => updateFilters(AscOrdersFilterAndSortField.ORDER, value)}
        />
        {!isCdeIt && (
          <InputField
            value={filters.get(AscOrdersFilterAndSortField.DELIVERY_NUMBER)}
            placeholder={t('order.order_detail.delivery_number', 'Delivery number')}
            onChangeDebounced={(value) => updateFilters(AscOrdersFilterAndSortField.DELIVERY_NUMBER, value)}
          />
        )}
        {!isCdeIt && (
          <InputField
            value={filters.get(AscOrdersFilterAndSortField.SUPPLIER_NAME)}
            placeholder={t('order.order_detail.supplier_name', 'Supplier name')}
            onChangeDebounced={(value) => updateFilters(AscOrdersFilterAndSortField.SUPPLIER_NAME, value)}
          />
        )}
        <InputField
          value={filters.get(AscOrdersFilterAndSortField.REFERENCE_NUMBER)}
          placeholder={t('asc_order.reference', 'Reference')}
          onChangeDebounced={(value) => updateFilters(AscOrdersFilterAndSortField.REFERENCE_NUMBER, value)}
        />
        <InputField
          value={filters.get(AscOrdersFilterAndSortField.INVOICE_NUMBER)}
          placeholder={t('order.invoice_number', 'Invoice number')}
          onChangeDebounced={(value) => updateFilters(AscOrdersFilterAndSortField.INVOICE_NUMBER, value)}
        />
        {!isCdeIt && (
          <Select
            onChange={(value: string) => {
              updateFilters(
                AscOrdersFilterAndSortField.STATUS,
                filters.get(AscOrdersFilterAndSortField.STATUS) === value ? '' : value,
              );
            }}
            allowClear
            styleLess
            placeholder={t('asc_order.status', 'Order status')}
            value={filters.get(AscOrdersFilterAndSortField.STATUS)}
            options={[
              {
                title: t('asc_order.status.new', 'New'),
                value: AscOrderStatus.NEW,
              },
              {
                title: t('asc_order.status.in_progress', 'In progress'),
                value: AscOrderStatus.IN_PROGRESS,
              },
              {
                title: t('asc_order.status.partially_delivered', 'Partially delivered'),
                value: AscOrderStatus.PARTIALLY_DELIVERED,
              },
              {
                title: t('asc_order.status.delivered', 'Delivered'),
                value: AscOrderStatus.DELIVERED,
              },
            ]}
            bordered
          />
        )}
        <Flex minWidth={360} maxWidth={360}>
          <RangePicker
            onChange={(dateStrings) => handleRangeFilterChange(dateStrings, updateFilters)}
            value={[filters.get(DATE_FROM) as string, filters.get(DATE_TO) as string]}
            allowClear={!isCdeIt}
            disabled={isCdeIt && hasOtherFilters}
          />
        </Flex>
      </Flex>
    </FlexWrapper>
  );
};

export default AscOrdersFilter;
